import React, { useState, useEffect } from 'react';
import { X, CheckCircle, Clock, User } from 'lucide-react';
import { alertManagementService } from '../../../services/alertManagementService';

const IncidentDetailModal = ({ incident, onClose, onResolve, onAcknowledge }) => {
  const [fullIncident, setFullIncident] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [showResolveForm, setShowResolveForm] = useState(false);

  useEffect(() => {
    loadIncidentDetails();
  }, [incident?.id]);

  const loadIncidentDetails = async () => {
    try {
      setLoading(true);
      const data = await alertManagementService?.getIncidentById(incident?.id);
      setFullIncident(data);
    } catch (error) {
      console.error('Error loading incident details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async () => {
    if (!resolutionNotes?.trim()) {
      alert('Please provide resolution notes');
      return;
    }
    await onResolve?.(incident?.id, resolutionNotes);
    onClose?.();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleString();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Incident Details</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="p-12 text-center text-gray-500">Loading...</div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Service</label>
                <p className="text-lg font-semibold text-gray-900 capitalize">
                  {fullIncident?.service_name}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Incident Type</label>
                <p className="text-lg font-semibold text-gray-900">
                  {fullIncident?.incident_type}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Severity</label>
                <p className="text-lg font-semibold text-gray-900 capitalize">
                  {fullIncident?.severity}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Status</label>
                <p className="text-lg font-semibold text-gray-900 capitalize">
                  {fullIncident?.status}
                </p>
              </div>
            </div>

            {/* Error Message */}
            <div>
              <label className="text-sm font-medium text-gray-500">Error Message</label>
              <p className="mt-1 text-gray-900 bg-gray-50 p-3 rounded-lg">
                {fullIncident?.error_message || 'No error message'}
              </p>
            </div>

            {/* Timeline */}
            <div>
              <label className="text-sm font-medium text-gray-500 mb-3 block">Timeline</label>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Detected</p>
                    <p className="text-sm text-gray-600">{formatDate(fullIncident?.detected_at)}</p>
                  </div>
                </div>

                {fullIncident?.acknowledged_at && (
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Acknowledged</p>
                      <p className="text-sm text-gray-600">{formatDate(fullIncident?.acknowledged_at)}</p>
                      {fullIncident?.acknowledged_by_user && (
                        <p className="text-sm text-gray-500">
                          by {fullIncident?.acknowledged_by_user?.full_name || fullIncident?.acknowledged_by_user?.email}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {fullIncident?.resolved_at && (
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-500 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Resolved</p>
                      <p className="text-sm text-gray-600">{formatDate(fullIncident?.resolved_at)}</p>
                      {fullIncident?.resolved_by_user && (
                        <p className="text-sm text-gray-500">
                          by {fullIncident?.resolved_by_user?.full_name || fullIncident?.resolved_by_user?.email}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Acknowledgments */}
            {fullIncident?.acknowledgments?.length > 0 && (
              <div>
                <label className="text-sm font-medium text-gray-500 mb-3 block">Acknowledgment History</label>
                <div className="space-y-2">
                  {fullIncident?.acknowledgments?.map((ack) => (
                    <div key={ack?.id} className="bg-gray-50 p-3 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <User className="w-4 h-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900">
                          {ack?.user?.full_name || ack?.user?.email}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatDate(ack?.acknowledged_at)}
                        </span>
                      </div>
                      {ack?.notes && (
                        <p className="text-sm text-gray-600 ml-6">{ack?.notes}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Resolution Notes */}
            {fullIncident?.resolution_notes && (
              <div>
                <label className="text-sm font-medium text-gray-500">Resolution Notes</label>
                <p className="mt-1 text-gray-900 bg-gray-50 p-3 rounded-lg">
                  {fullIncident?.resolution_notes}
                </p>
              </div>
            )}

            {/* Resolve Form */}
            {showResolveForm && fullIncident?.status !== 'resolved' && (
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Resolution Notes
                </label>
                <textarea
                  value={resolutionNotes}
                  onChange={(e) => setResolutionNotes(e?.target?.value)}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Describe how this incident was resolved..."
                />
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
              {fullIncident?.status === 'open' && (
                <button
                  onClick={() => {
                    onAcknowledge?.(incident?.id);
                    onClose?.();
                  }}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
                >
                  Acknowledge
                </button>
              )}
              {fullIncident?.status !== 'resolved' && (
                <>
                  {!showResolveForm ? (
                    <button
                      onClick={() => setShowResolveForm(true)}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      Resolve Incident
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => setShowResolveForm(false)}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleResolve}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      >
                        Confirm Resolution
                      </button>
                    </>
                  )}
                </>
              )}
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IncidentDetailModal;